import React, { useMemo } from 'react'

import { TDashboardGroup } from 'types/GlobalDashboardGroup'

import { useTranslation } from 'language/translator'
import { useAccounts } from 'redux/hooks/Accounts'
import { useCompanyGroups } from 'redux/hooks/CompanyGroups'
import { useAllowEditDashboardGroup } from 'redux/hooks/Permissions'

import Box from 'components_new/atoms/Box'
import Collapse from 'components_new/atoms/Collapse'
import Divider from 'components_new/atoms/Divider'
import FormControlLabel from 'components_new/atoms/FormControlLabel'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import List from 'components_new/atoms/List'
import Switch from 'components_new/atoms/Switch'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'

type NotAddedOption = Option & {
  body: string
  id: string
  title: string
}

interface ExternalProps {
  dashboardGroup: TDashboardGroup
  onInvite: (companyGroupId: string) => void
  onRemove: (companyGroupId: string) => void
  setAllCompanyGroupsHasAccess: (allHasAccess: boolean) => void
}

const CompanyGroups = (props: ExternalProps) => {
  const { dashboardGroup, onInvite, onRemove, setAllCompanyGroupsHasAccess } =
    props

  const { accounts } = useAccounts()
  const [allCompanyGroups] = useCompanyGroups()
  const allowEditDashboardGroup = useAllowEditDashboardGroup(dashboardGroup.id)
  const { t } = useTranslation()

  const allCompanyGroupsHasAccess =
    dashboardGroup.share_settings.all_company_groups_has_access ?? false

  const [addedCustomerOptions, notAddedCustomerOptions] = useMemo(() => {
    if (allCompanyGroupsHasAccess) {
      return [[], []]
    }

    // get all company groups which has access
    const permitted = new Set(
      dashboardGroup.share_settings.permissions
        .filter((p) => p.company_group_id)
        .map((p) => p.company_group_id)
    )

    // retreive number of users per company group id
    const numberOfUsersMapper = accounts.reduce((acc, account) => {
      if (!account.company_group_id) {
        return acc
      }

      acc[account.company_group_id] = (acc[account.company_group_id] || 0) + 1

      return acc
    }, {} as { [companyGroupId: string]: number })

    const options = allCompanyGroups.map((cg) => {
      return {
        ...cg,
        hasPermission: permitted.has(cg.id),
        numberOfUsers: numberOfUsersMapper[cg.id] ?? 0
      }
    })

    const added: typeof options = []
    const notAdded: typeof options = []

    for (const opt of options) {
      if (opt.hasPermission) {
        added.push(opt)
      } else {
        notAdded.push(opt)
      }
    }

    return [
      added,
      notAdded.map((option): NotAddedOption => {
        return {
          body: `${option.numberOfUsers} ${t(
            'global',
            option.numberOfUsers === 1 ? 'user' : 'users',
            true
          )}`,
          id: option.id,
          title: option.name
        }
      })
    ]
  }, [allCompanyGroups, dashboardGroup, allCompanyGroupsHasAccess])

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={allCompanyGroupsHasAccess}
              onChange={() =>
                setAllCompanyGroupsHasAccess(!allCompanyGroupsHasAccess)
              }
            />
          }
          label={t('company_groups', 'all_company_groups_have_access')}
          labelPlacement={'end'}
        />
      </Box>

      <Collapse in={!allCompanyGroupsHasAccess}>
        <Divider sx={{ my: 2 }} />
        <SelectSearch
          closeOnSelect={true}
          color="primary"
          disableClearable={true}
          label={t('company_groups', 'add_company_group')}
          onChange={(option) => {
            onInvite(option.id)
          }}
          multiple={false}
          options={notAddedCustomerOptions}
          selected={null}
          size={'medium'}
          sort={true}
          sx={{ mb: 2 }}
        />

        <List sx={{ pt: 0 }}>
          {addedCustomerOptions.map((option) => (
            <GenericListItem
              actions={
                allowEditDashboardGroup ? (
                  <IconButton onClick={() => onRemove(option.id)} size="small">
                    <Icon fontSize="small" name="RemoveCircleOutlineOutlined" />
                  </IconButton>
                ) : null
              }
              body={`${option.numberOfUsers} användare`}
              chip={{
                label: t('global', 'company_group')
              }}
              hasHover={true}
              key={option.id}
              title={option.name}
              variant="company"
            />
          ))}
        </List>
      </Collapse>
    </Box>
  )
}

export default CompanyGroups
